import { jsPDF } from 'jspdf';
import domtoimage from 'dom-to-image-chrome-fix-retina';

/**
 * Generates a PDF containing ID cards formatted as per specification:
 * - Page format: 21.0 cm width x 14.85 cm height (Landscape)
 * - Card dimensions: 5.5 cm width x 8.7 cm height
 * - Side-by-side arrangement: Back card on Left (5.5 x 8.7 cm), Front card on Right (5.5 x 8.7 cm)
 * - Multi-page: 1 page per student / ID card
 *
 * @param {Array} students - Array of student card objects (with mapped_id, is_verified, template, etc.)
 * @param {Object} options - Configuration options
 * @param {string} options.fileName - Output PDF file name
 * @param {Function} options.onProgress - Progress callback: (current, total, statusText) => void
 * @returns {Promise<boolean>}
 */
export const generateIdCardsPdf = async (students, options = {}) => {
  const {
    fileName = 'ID_Cards.pdf',
    onProgress = () => {}
  } = options;

  if (!students || students.length === 0) {
    throw new Error('No students provided for PDF generation');
  }

  const PAGE_WIDTH = 21.0; // cm
  const PAGE_HEIGHT = 14.85; // cm
  const CARD_WIDTH = 5.5; // cm
  const CARD_HEIGHT = 8.7; // cm

  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'cm',
    format: [PAGE_WIDTH, PAGE_HEIGHT]
  });

  const total = students.length;

  for (let i = 0; i < total; i++) {
    const student = students[i];
    const mappedId = student.mapped_id;

    if (onProgress) {
      onProgress(i + 1, total, `Processing card ${i + 1} of ${total}...`);
    }

    if (i > 0) {
      pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT], 'landscape');
    }

    const frontNode = document.getElementById(`previewcustomCard-front-${mappedId}`);
    const backNode = document.getElementById(`previewcustomCard-back-${mappedId}`);

    if (!frontNode) {
      console.warn(`Card DOM node for student ${mappedId} not found`);
      continue;
    }

    let frontDataUrl = null;
    let backDataUrl = null;

    try {
      frontDataUrl = await domtoimage.toPng(frontNode, { cacheBust: true });
    } catch (err) {
      console.error(`Error capturing front card for ${mappedId}:`, err);
    }

    if (backNode) {
      try {
        backDataUrl = await domtoimage.toPng(backNode, { cacheBust: true });
      } catch (err) {
        console.error(`Error capturing back card for ${mappedId}:`, err);
      }
    }

    // Determine dimensions & position
    // Default is portrait: 5.5 cm wide x 8.7 cm high
    const isBothSide = !!backDataUrl;

    if (isBothSide) {
      // Both side card: Left is BACK, Right is FRONT
      // Total width = 5.5 + 5.5 = 11.0 cm
      // Left margin = (21.0 - 11.0) / 2 = 5.0 cm
      // Top margin = (14.85 - 8.7) / 2 = 3.075 cm
      const xBack = 5.0;
      const yBack = 3.075;
      const xFront = 10.5;
      const yFront = 3.075;

      if (backDataUrl) {
        pdf.addImage(backDataUrl, 'PNG', xBack, yBack, CARD_WIDTH, CARD_HEIGHT);
      }
      if (frontDataUrl) {
        pdf.addImage(frontDataUrl, 'PNG', xFront, yFront, CARD_WIDTH, CARD_HEIGHT);
      }
    } else {
      // Single side card: Centered on page
      // Left margin = (21.0 - 5.5) / 2 = 7.75 cm
      // Top margin = (14.85 - 8.7) / 2 = 3.075 cm
      const xFront = 7.75;
      const yFront = 3.075;

      if (frontDataUrl) {
        pdf.addImage(frontDataUrl, 'PNG', xFront, yFront, CARD_WIDTH, CARD_HEIGHT);
      }
    }
  }

  // Save PDF
  const finalFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
  pdf.save(finalFileName);

  return true;
};
